import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Target, 
  Users, 
  School, 
  ChefHat, 
  MapPin, 
  Building2, 
  Compass,
  Handshake,
  HeartHandshake,
  Star, 
  Bell, 
  Shield, 
  Network, 
  Layout,
  CheckCircle2,
  TrendingUp,
  Menu,
  X,
  Sun,
  Moon,
  CreditCard,
  Lock,
  FileText,
  Link,
  Eye,
  Clock,
  Award,
  Zap,
  Mail,
  Gift,
  Heart,
  Scale,
  RefreshCw,
  MessageSquare,
  BarChart3,
  AlertTriangle,
  Download,
  Settings,
  ChevronLeft,
  ChevronRight,
  Utensils,
  Baby,
  Landmark,
  Monitor,
  Laptop,
  Smartphone,
  Lightbulb,
  ClipboardList,
  PieChart,
  Pencil,
  Rocket,
  UtensilsCrossed
} from 'lucide-react';
import { VisionSectionEN, ConclusionSectionEN } from './MediaKitEN';
import { 
  ArchitectureSectionEN, 
  CatererSectionEN, 
  ParentSectionEN,
  CoParentingSectionEN,
  SchoolSectionEN,
  RegionSectionEN,
  SSCSectionEN,
  EvaluationSectionEN,
  AlertsSectionEN,
  SecuritySectionEN,
  FlowSectionEN
} from './AllSectionsEN';

const MediaKit = () => {
  const [activeSection, setActiveSection] = useState('vision');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState('fr'); // 'fr' or 'en'
  const [theme, setTheme] = useState('dark'); // 'light' or 'dark'
  const [selectedPortal, setSelectedPortal] = useState(null); // For portal details modal

  const sections = language === 'fr' ? [
    { id: 'vision', label: 'Vision & Objectifs', icon: Target, color: 'from-green-500 to-emerald-600' },
    { id: 'architecture', label: 'Architecture Globale', icon: Layout, color: 'from-blue-500 to-cyan-600' },
    { id: 'css', label: 'Portail CSS', icon: Landmark, color: 'from-red-500 to-rose-600' },
    { id: 'region', label: 'Portail Direction d\'École', icon: Building2, color: 'from-teal-500 to-green-600' },
    { id: 'ecole', label: 'Portail École', icon: School, color: 'from-indigo-500 to-blue-600' },
    { id: 'traiteur', label: 'Portail Traiteur', icon: ChefHat, color: 'from-orange-500 to-amber-600' },
    { id: 'parent', label: 'Portail Parent/Enfant', icon: Users, color: 'from-purple-500 to-pink-600' },
    { id: 'coparentalite', label: 'Co-Parentalité', icon: HeartHandshake, color: 'from-cyan-500 to-indigo-600' },
    { id: 'evaluation', label: 'Système d\'Évaluation', icon: Star, color: 'from-yellow-500 to-orange-600' },
    { id: 'alertes', label: 'Alertes & Rapports', icon: Bell, color: 'from-rose-500 to-pink-600' },
    { id: 'securite', label: 'Sécurité', icon: Shield, color: 'from-gray-500 to-slate-600' },
    { id: 'flux', label: 'Flux d\'Information', icon: Network, color: 'from-cyan-500 to-blue-600' },
    { id: 'conclusion', label: 'Conclusion', icon: CheckCircle2, color: 'from-green-500 to-teal-600' }
  ] : [
    { id: 'vision', label: 'Vision & Goals', icon: Target, color: 'from-green-500 to-emerald-600' },
    { id: 'architecture', label: 'Global Architecture', icon: Layout, color: 'from-blue-500 to-cyan-600' },
    { id: 'css', label: 'School Board Portal', icon: Landmark, color: 'from-red-500 to-rose-600' },
    { id: 'region', label: 'School Direction Portal', icon: Building2, color: 'from-teal-500 to-green-600' },
    { id: 'ecole', label: 'School Portal', icon: School, color: 'from-indigo-500 to-blue-600' },
    { id: 'traiteur', label: 'Caterer Portal', icon: ChefHat, color: 'from-orange-500 to-amber-600' },
    { id: 'parent', label: 'Parent/Child Portal', icon: Users, color: 'from-purple-500 to-pink-600' },
    { id: 'coparentalite', label: 'Co-Parenting', icon: HeartHandshake, color: 'from-cyan-500 to-indigo-600' },
    { id: 'evaluation', label: 'Evaluation System', icon: Star, color: 'from-yellow-500 to-orange-600' },
    { id: 'alertes', label: 'Alerts & Reports', icon: Bell, color: 'from-rose-500 to-pink-600' },
    { id: 'securite', label: 'Security', icon: Shield, color: 'from-gray-500 to-slate-600' },
    { id: 'flux', label: 'Information Flow', icon: Network, color: 'from-cyan-500 to-blue-600' },
    { id: 'conclusion', label: 'Conclusion', icon: CheckCircle2, color: 'from-green-500 to-teal-600' }
  ];

  const getCurrentSection = () => sections.find(s => s.id === activeSection) || sections[0];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900' 
        : 'bg-gradient-to-br from-slate-50 via-white to-green-50'
    }`}>
      {/* Fox mosaic brand pattern overlay */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `url('/fox-pattern-transparent.png')`,
          backgroundRepeat: 'repeat',
          backgroundSize: '90px 90px',
          opacity: theme === 'dark' ? 0.15 : 0.05
        }}
      />
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-sm border-b shadow-sm transition-colors duration-300 ${
        theme === 'dark'
          ? 'bg-gray-800/95 border-gray-700'
          : 'bg-white/95 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img 
                src="/ecolunch-logo.png" 
                alt="EcoLuncH Logo" 
                className="h-10 sm:h-16 w-auto object-contain"
              />
              <div className="hidden sm:block">
                <p className={`text-xs font-medium transition-colors duration-300 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>{language === 'fr' ? 'La révolution alimentaire scolaire au Québec' : 'The School Nutrition Revolution in Quebec'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Theme Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setTheme('light')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    theme === 'light' 
                      ? 'bg-purple-600 text-white shadow-sm' 
                      : 'bg-white text-gray-600 hover:text-gray-900'
                  }`}
                  title="Mode clair"
                >
                  <Sun className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    theme === 'dark' 
                      ? 'bg-purple-600 text-white shadow-sm' 
                      : 'bg-white text-gray-600 hover:text-gray-900'
                  }`}
                  title="Mode sombre"
                >
                  <Moon className="w-4 h-4" />
                </button>
              </div>
              {/* Language Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                    language === 'fr' 
                      ? 'bg-purple-600 text-white shadow-sm' 
                      : 'bg-white text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span>FR</span>
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                    language === 'en' 
                      ? 'bg-purple-600 text-white shadow-sm' 
                      : 'bg-white text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span>EN</span>
                </button>
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={`lg:hidden fixed inset-0 z-40 mt-16 overflow-y-auto transition-colors duration-300 ${
          theme === 'dark' ? 'bg-gray-900' : 'bg-white'
        }`}>
          <div className="p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r ' + section.color + ' text-white shadow-lg'
                      : theme === 'dark'
                        ? 'hover:bg-gray-700 text-gray-300'
                        : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex max-w-7xl mx-auto relative z-10">
        {/* Sidebar Navigation - Desktop */}
        <aside className={`hidden lg:block w-72 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto border-r backdrop-blur-sm transition-colors duration-300 ${
          theme === 'dark'
            ? 'border-gray-700 bg-gray-800/50'
            : 'border-gray-200 bg-white/50'
        }`}>
          <nav className="p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => { setActiveSection(section.id); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all transform hover:scale-105 ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r ' + section.color + ' text-white shadow-lg'
                      : theme === 'dark'
                        ? 'hover:bg-gray-700 text-gray-300'
                        : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-5xl mx-auto">
            {/* Navigation arrows */}
            {(() => {
              const currentIndex = sections.findIndex(s => s.id === activeSection);
              const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
              const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;
              const currentLabel = sections[currentIndex]?.label || '';
              return (
                <div className={`flex items-center justify-between mb-6 p-3 rounded-xl border transition-colors duration-300 ${
                  theme === 'dark' ? 'bg-gray-800/60 border-gray-700' : 'bg-white/80 border-gray-200'
                }`}>
                  <button
                    onClick={() => { prevSection && setActiveSection(prevSection.id); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                    disabled={!prevSection}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      prevSection
                        ? theme === 'dark' ? 'hover:bg-gray-700 text-cyan-400' : 'hover:bg-gray-100 text-blue-600'
                        : 'opacity-30 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm hidden sm:inline">{prevSection?.label || ''}</span>
                  </button>
                  <span className={`text-sm font-bold px-4 ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-700'
                  }`}>{currentLabel}</span>
                  <button
                    onClick={() => { nextSection && setActiveSection(nextSection.id); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                    disabled={!nextSection}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      nextSection
                        ? theme === 'dark' ? 'hover:bg-gray-700 text-cyan-400' : 'hover:bg-gray-100 text-blue-600'
                        : 'opacity-30 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    <span className="text-sm hidden sm:inline">{nextSection?.label || ''}</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              );
            })()}
            {/* All Sections with Language Support */}
            <div key={`${activeSection}-${language}`} className="min-h-[60vh]">
            {activeSection === 'vision' && (language === 'fr' ? <VisionSection /> : <VisionSectionEN />)}
            {activeSection === 'architecture' && (language === 'fr' ? <ArchitectureSection /> : <ArchitectureSectionEN />)}
            {activeSection === 'traiteur' && (language === 'fr' ? <TraiteurSection /> : <CatererSectionEN />)}
            {activeSection === 'parent' && (language === 'fr' ? <ParentSection /> : <ParentSectionEN />)}
            {activeSection === 'coparentalite' && (language === 'fr' ? <CoParentaliteSection /> : <CoParentingSectionEN />)}
            {activeSection === 'ecole' && (language === 'fr' ? <EcoleSection /> : <SchoolSectionEN />)}
            {activeSection === 'region' && (language === 'fr' ? <RegionSection /> : <RegionSectionEN />)}
            {activeSection === 'css' && (language === 'fr' ? <CSSSection /> : <SSCSectionEN />)}
            {activeSection === 'evaluation' && (language === 'fr' ? <EvaluationSection /> : <EvaluationSectionEN />)}
            {activeSection === 'alertes' && (language === 'fr' ? <AlertesSection /> : <AlertsSectionEN />)}
            {activeSection === 'securite' && (language === 'fr' ? <SecuriteSection /> : <SecuritySectionEN />)}
            {activeSection === 'flux' && (language === 'fr' ? <FluxSection /> : <FlowSectionEN />)}
            {activeSection === 'conclusion' && (language === 'fr' ? <ConclusionSection /> : <ConclusionSectionEN />)}
            </div>

            {/* Footer navigation arrows */}
            {(() => {
              const currentIndex = sections.findIndex(s => s.id === activeSection);
              const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
              const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;
              const currentLabel = sections[currentIndex]?.label || '';
              return (
                <div className={`flex items-center justify-between mt-6 p-3 rounded-xl border transition-colors duration-300 ${
                  theme === 'dark' ? 'bg-gray-800/60 border-gray-700' : 'bg-white/80 border-gray-200'
                }`}>
                  <button
                    onClick={() => { prevSection && setActiveSection(prevSection.id); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                    disabled={!prevSection}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      prevSection
                        ? theme === 'dark' ? 'hover:bg-gray-700 text-cyan-400' : 'hover:bg-gray-100 text-blue-600'
                        : 'opacity-30 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm hidden sm:inline">{prevSection?.label || ''}</span>
                  </button>
                  <span className={`text-sm font-bold px-4 ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-700'
                  }`}>{currentLabel}</span>
                  <button
                    onClick={() => { nextSection && setActiveSection(nextSection.id); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                    disabled={!nextSection}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      nextSection
                        ? theme === 'dark' ? 'hover:bg-gray-700 text-cyan-400' : 'hover:bg-gray-100 text-blue-600'
                        : 'opacity-30 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    <span className="text-sm hidden sm:inline">{nextSection?.label || ''}</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              );
            })()}
          </div>
        </main>
      </div>
    </div>
  );
};

// Section Components
const VisionSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1519389950473-47ba0277781c" 
        alt="Vision" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-emerald-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6 flex flex-col md:flex-row items-center gap-6">
          <img 
            src="/eco-fox-head.png"
            alt="Mascotte EcoLuncH"
            className="w-24 h-24 object-contain"
          />
          <div>
            <h2 className="text-4xl font-bold mb-3">Vision & Objectifs</h2>
            <p className="text-xl font-semibold">La révolution alimentaire scolaire au Québec</p>
          </div>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-lg">
      <CardContent className="p-0 overflow-hidden rounded-lg">
        <video 
          controls 
          className="w-full"
          preload="metadata"
        >
          <source src="https://customer-assets.emergentagent.com/job_7f252a75-97cc-4ed7-9549-a7fb574469a2/artifacts/xg29f4mx_Pr%C3%A9sentation%20Ecolunch.mp4" type="video/mp4" />
        </video>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Target className="w-6 h-6 text-green-600" />
          <span>Objectif Général</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-lg leading-relaxed text-gray-700">
          EcoLuncH est une <strong className="text-green-600">infrastructure numérique révolutionnaire</strong> dédiée à transformer durablement la gestion, la distribution et le suivi de la qualité des repas scolaires <strong className="text-green-700">à la grandeur du Québec</strong>.
        </p>
        <p className="text-lg leading-relaxed text-gray-700">
          Elle vise à <strong className="text-emerald-600">centraliser, automatiser et simplifier</strong> l'ensemble du secteur en réunissant, au sein d'un même environnement, les parents, les écoles, les traiteurs et les centres de services scolaires (CSS). En créant un cadre commun, standardisé, fluide, transparent et mesurable, EcoLuncH positionne les repas scolaires dans une logique uniforme à l'échelle du Québec et <strong className="text-green-600">s'impose comme l'infrastructure de référence du marché</strong>.
        </p>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-green-600">Description Fonctionnelle</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            EcoLuncH agit comme une <strong>plateforme de gouvernance alimentaire scolaire</strong> reposant sur un modèle multi-niveaux :
          </p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <Badge className="bg-purple-500"><Users className="w-4 h-4" /></Badge>
              <span>Parent/Enfant : interface familiale intuitive</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-orange-500"><ChefHat className="w-4 h-4" /></Badge>
              <span>Traiteur : back-office de production</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-indigo-500"><School className="w-4 h-4" /></Badge>
              <span>École : cockpit de suivi et communication</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-teal-500"><Building2 className="w-4 h-4" /></Badge>
              <span>Directions d'École : superviser une ou plusieurs écoles / établissements</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-red-500"><Landmark className="w-4 h-4" /></Badge>
              <span>CSS : gouvernance globale</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-emerald-600">Principes Fondamentaux</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Automatisation intelligente</strong>
                <p className="text-sm text-gray-600">Réduction du travail manuel et double saisie</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Transparence totale</strong>
                <p className="text-sm text-gray-600">Traçabilité complète de chaque repas</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Hiérarchie fluide</strong>
                <p className="text-sm text-gray-600">Circulation contrôlée de l'information</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Sécurité absolue</strong>
                <p className="text-sm text-gray-600">Données cloisonnées par entité</p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
      <CardHeader>
        <CardTitle className="text-xl">Objectifs par Acteur</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-green-200">
                <th className="text-left p-3 font-semibold">Acteur</th>
                <th className="text-left p-3 font-semibold">Objectif Principal</th>
                <th className="text-left p-3 font-semibold">Valeur Ajoutée</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-green-100">
              <tr>
                <td className="p-3"><Users className="w-4 h-4 inline text-cyan-400" /> Parents</td>
                <td className="p-3">Commander rapidement</td>
                <td className="p-3 text-green-600 font-medium">Gain de temps, visibilité</td>
              </tr>
              <tr>
                <td className="p-3"><ChefHat className="w-4 h-4 inline text-orange-400" /> Traiteurs</td>
                <td className="p-3">Automatiser la gestion</td>
                <td className="p-3 text-green-600 font-medium">Efficience opérationnelle</td>
              </tr>
              <tr>
                <td className="p-3"><School className="w-4 h-4 inline text-green-400" /> Écoles</td>
                <td className="p-3">Surveiller la satisfaction</td>
                <td className="p-3 text-green-600 font-medium">Outil de pilotage</td>
              </tr>
              <tr>
                <td className="p-3"><Building2 className="w-4 h-4 inline text-teal-400" /> Directions d'École</td>
                <td className="p-3">Superviser une ou plusieurs écoles</td>
                <td className="p-3 text-green-600 font-medium">Vue consolidée</td>
              </tr>
              <tr>
                <td className="p-3"><Landmark className="w-4 h-4 inline text-red-400" /> CSS</td>
                <td className="p-3">Gouverner et auditer</td>
                <td className="p-3 text-green-600 font-medium">Vision stratégique</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-xl">Exemple d'Usage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <p className="text-gray-700">Un parent commande pour deux enfants en <strong className="text-purple-600">15 secondes</strong> via l'application mobile</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <p className="text-gray-700">Le traiteur reçoit la commande <strong className="text-orange-600">automatiquement</strong> intégrée à sa fiche de production</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <p className="text-gray-700">L'école voit le volume ajusté dans son tableau de bord <strong className="text-indigo-600">en temps réel</strong></p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <p className="text-gray-700">Le CSS constate une hausse de participation de <strong className="text-red-600">xx%</strong> dans la région</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const ArchitectureSection = () => {
  const [selectedPortal, setSelectedPortal] = useState(null);
  
  return (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1523240795612-9a054b0db644" 
        alt="Architecture" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-cyan-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Architecture Globale</h2>
          <p className="text-xl">5 portails interconnectés, 1 back-end central</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <Layout className="w-7 h-7 text-white" />
          </div>
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-bold">Architecture Multi-Portails</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-lg text-gray-700">
          EcoLuncH repose sur une <strong className="text-blue-600">architecture multi-portails interconnectée</strong> avec un back-end central intelligent.
        </p>

        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-8 rounded-2xl border-2 border-blue-400 shadow-2xl relative overflow-hidden">
          {/* Fox mosaic pattern in architecture blue */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url('/fox-pattern-transparent.png')`,
              backgroundRepeat: 'repeat',
              backgroundSize: '70px 70px',
              opacity: 0.12
            }}
          />
          <div className="text-center space-y-6">
            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('css')}
            >
              <div className="bg-gradient-to-br from-red-500 to-red-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-red-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-red-400 hover:border-red-300">
                <Landmark className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">CSS (siège)</div>
                <div className="text-sm">Gouvernance & politique</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-red-400 via-pink-400 to-teal-500 shadow-lg shadow-pink-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('directions')}
            >
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-teal-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-teal-400 hover:border-teal-300">
                <Building2 className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Directions d'école</div>
                <div className="text-sm">Supervision multi-écoles</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-teal-400 via-blue-400 to-indigo-500 shadow-lg shadow-indigo-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('ecoles')}
            >
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-indigo-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-indigo-400 hover:border-indigo-300">
                <School className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Écoles locales</div>
                <div className="text-sm">Cockpit de suivi et communication</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-indigo-400 via-amber-400 to-orange-500 shadow-lg shadow-orange-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('traiteurs')}
            >
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-orange-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-orange-400 hover:border-orange-300">
                <ChefHat className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Traiteurs</div>
                <div className="text-sm">Back-office de production</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-orange-400 via-pink-400 to-purple-500 shadow-lg shadow-purple-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('parents')}
            >
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-purple-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-purple-400 hover:border-purple-300">
                <Users className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Parents / Enfants</div>
                <div className="text-sm">Commandes, paiements, feedback</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-purple-400 via-cyan-400 to-cyan-500 shadow-lg shadow-cyan-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('coparentalite')}
            >
              <div className="bg-gradient-to-br from-cyan-500 to-indigo-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-cyan-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-cyan-400 hover:border-cyan-300">
                <HeartHandshake className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Co-Parentalité</div>
                <div className="text-sm">Module de gestion collaborative</div>
                <div className="text-xs mt-2 opacity-70 italic">cliquer pour retourner</div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-gradient-to-r from-slate-800 to-slate-700 p-4 rounded-xl shadow-lg border border-blue-400/30">
            <p className="text-center text-white font-medium">
              <strong className="text-cyan-400"><Lightbulb className="w-4 h-4 inline text-yellow-400" /> Cliquez sur chaque portail</strong>
              <span className="block text-sm mt-1 text-gray-300">pour découvrir les détails de son interface frontend</span>
            </p>
          </div>
          
          <div className="mt-6 bg-white/10 backdrop-blur-sm p-4 rounded-xl shadow-md border border-blue-300/50">
            <p className="text-center text-white font-medium text-sm">
              <strong className="text-blue-300">Caractéristiques clés :</strong>
              <span className="inline-block bg-blue-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-blue-400">Temps réel</span>
              <span className="inline-block bg-indigo-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-indigo-400">Sécurisé</span>
              <span className="inline-block bg-purple-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-purple-400">Synchronisé</span>
            </p>
          </div>
        </div>

        {/* Modal Overlay for Portal Details */}
        {selectedPortal && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPortal(null)}
          >
            <div 
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl max-w-lg w-full border-2 relative"
              style={{
                borderColor: selectedPortal === 'css' ? '#ef4444' :
                            selectedPortal === 'directions' ? '#14b8a6' :
                            selectedPortal === 'ecoles' ? '#6366f1' :
                            selectedPortal === 'traiteurs' ? '#f97316' :
                            selectedPortal === 'parents' ? '#a855f7' :
                            '#06b6d4'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Section Indicator Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white text-xs font-bold shadow-lg">
                PORTAIL CLIQUÉ
              </div>

              {/* Close Button */}
              <button 
                onClick={() => setSelectedPortal(null)}
                className="absolute top-3 right-3 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="p-5">
                {selectedPortal === 'css' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                        <Landmark className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">CSS (siège)</h3>
                        <p className="text-sm text-red-300">Gouvernance & politique</p>
                      </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Dashboard exécutif temps réel</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Gestion multi-écoles & directions</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Rapports consolidés & KPIs</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Configuration des politiques</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Analytics & Business Intelligence</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'directions' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Directions d'école</h3>
                        <p className="text-sm text-teal-300">Supervision multi-écoles</p>
                      </div>
                    </div>
                    <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Vue multi-écoles unifiée</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Dashboard de supervision régionale</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Gestion des directeurs d'école</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Rapports comparatifs inter-écoles</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Budget & allocation ressources</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'ecoles' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <School className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Écoles locales</h3>
                        <p className="text-sm text-indigo-300">Cockpit, alertes</p>
                      </div>
                    </div>
                    <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Cockpit de gestion quotidienne</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Suivi en temps réel des commandes</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Alertes & notifications push</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Gestion des élèves & absences</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Rapports d'activité école</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'traiteurs' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                        <ChefHat className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Traiteurs</h3>
                        <p className="text-sm text-orange-300">Back-office</p>
                      </div>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Back-office de production complète</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Gestion menus & planification</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Suivi commandes & modifications</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Gestion stock & inventaire</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Facturation & paiements</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'parents' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Parents / Enfants</h3>
                        <p className="text-sm text-purple-300">Commandes, paiements, feedback</p>
                      </div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <div className="font-bold text-purple-300 mb-3 text-sm flex items-center space-x-2">
                        <span className="flex space-x-1"><Monitor className="w-4 h-4 text-cyan-400" /><Laptop className="w-4 h-4 text-cyan-400" /><Smartphone className="w-4 h-4 text-cyan-400" /></span>
                        <span>Multi-Plateforme</span>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Interface Web responsive</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Application mobile Android/iOS</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Mode Parent: gestion complète</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Mode Enfant: interface ludique</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Commande rapide multi-enfants</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Paiements flexibles & wallet</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'coparentalite' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <HeartHandshake className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Co-Parentalité</h3>
                        <p className="text-sm text-cyan-300">Module de gestion collaborative</p>
                      </div>
                    </div>
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                      <div className="font-bold text-cyan-300 mb-3 text-sm flex items-center space-x-2">
                        <span className="flex space-x-1"><Monitor className="w-4 h-4 text-cyan-400" /><Laptop className="w-4 h-4 text-cyan-400" /><Smartphone className="w-4 h-4 text-cyan-400" /></span>
                        <span>Extension Parent Portal</span>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Interface Web + Mobile dédiée</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Dashboard partagé 2 parents</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Permissions granulaires configurables</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Split paiement automatique</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Page de paiement tiers (sans compte)</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Historique & audit complet</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-blue-600">Le Back-end Central</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">Le cœur du système :</p>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Gère la base de données commune et droits d'accès</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Synchronisation en temps réel</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Filtre les échanges selon les permissions (RBAC)</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <span>Alimente les rapports et statistiques</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-cyan-600">Sécurité Structurelle</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Architecture multi-tenant isolée</strong>
                <p className="text-sm text-gray-600">Chaque entité dans son environnement</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Données partagées filtrées</strong>
                <p className="text-sm text-gray-600">Uniquement ce qui est nécessaire</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Aucune donnée brute transmise</strong>
                <p className="text-sm text-gray-600">Protection des informations sensibles</p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-xl">Exemple d'Interaction</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
            <div>
              <p className="font-semibold text-gray-800">Parent commande</p>
              <p className="text-sm text-gray-600">La commande est stockée dans le back-end central</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
            <div>
              <p className="font-semibold text-gray-800">Traiteur reçoit</p>
              <p className="text-sm text-gray-600">L'ordre de production est transmis automatiquement</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
            <div>
              <p className="font-semibold text-gray-800">École visualise</p>
              <p className="text-sm text-gray-600">Les volumes du jour sont mis à jour en temps réel</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
            <div>
              <p className="font-semibold text-gray-800">CSS consulte</p>
              <p className="text-sm text-gray-600">Les statistiques des portefeuilles d'écoles sont disponibles instantanément</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white rounded-lg border-2 border-blue-200">
            <p className="text-center text-blue-600 font-bold">Tout se fait en temps réel, sans double saisie</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
  );
};

const CoParentaliteSection = () => (
  <div className="space-y-6 animate-fadeIn">
    {/* Hero */}
    <div className="relative overflow-hidden rounded-2xl shadow-2xl">
      <div className="bg-gradient-to-r from-cyan-600 via-indigo-600 to-purple-600 py-12">
        <div className="text-center text-white p-6">
          <HeartHandshake className="w-16 h-16 mx-auto mb-4 opacity-90" />
          <h2 className="text-4xl font-bold mb-3">Module Co-Parentalité</h2>
          <p className="text-xl opacity-90">Module de gestion collaborative pour familles modernes</p>
        </div>
      </div>
    </div>

    {/* Module de Gestion Collaborative */}
    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <HeartHandshake className="w-6 h-6 text-blue-600" />
          <span>Module de Gestion Collaborative</span>
        </CardTitle>
        <CardDescription>Accès partagé et permissions configurables pour une co-parentalité harmonieuse</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border border-blue-200">
            <h4 className="font-bold text-blue-700 mb-3 flex items-center space-x-2">
              <Users className="w-5 h-5" />
              <span>Accès Multi-Parents</span>
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Invitation via lien unique</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Validation double approbation</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Synchronisation temps réel</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl border border-purple-200">
            <h4 className="font-bold text-purple-700 mb-3 flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Permissions Configurables</span>
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Qui peut commander</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Qui peut consulter</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Qui peut payer</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-5 rounded-xl border border-orange-200">
            <h4 className="font-bold text-orange-700 mb-3 flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Sécurité Renforcée</span>
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Paiements chiffrés séparés</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Journal d'activité complet</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Traçabilité IP et horodatage</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-slate-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center space-x-2">
              <Bell className="w-5 h-5" />
              <span>Notifications Intelligentes</span>
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Alertes temps réel</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Approbations interactives</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Historique des actions</span></li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Partage des Paiements */}
    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <CreditCard className="w-6 h-6 text-orange-600" />
          <span>Partage des Paiements</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"><BarChart3 className="w-5 h-5 text-blue-600" /></div>
          <div><p className="font-bold text-blue-700">Split Automatique</p><p className="text-sm text-gray-600">Options prédéfinies : 50/50, 60/40, 70/30 ou pourcentage personnalisé</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
          <div><p className="font-bold text-green-700">Validation Double Approbation</p><p className="text-sm text-gray-600">Les deux parents doivent approuver la répartition avant activation</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-xl">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0"><Lock className="w-5 h-5 text-yellow-600" /></div>
          <div><p className="font-bold text-yellow-700">Paiements Chiffrés Séparés</p><p className="text-sm text-gray-600">Chaque parent entre son moyen de paiement. Aucun ne voit les infos de l'autre</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-purple-600" /></div>
          <div><p className="font-bold text-purple-700">Factures Individuelles</p><p className="text-sm text-gray-600">Reçus séparés automatiques. Rapport PDF annuel exportable</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-cyan-50 rounded-xl">
          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0"><Link className="w-5 h-5 text-cyan-600" /></div>
          <div><p className="font-bold text-cyan-700">Lien de Paiement Unique</p><p className="text-sm text-gray-600">Envoyez un lien de paiement à des tiers (grands-parents, famille, amis) pour contribution ponctuelle sans inscription</p></div>
        </div>
      </CardContent>
    </Card>

    {/* Lien de Paiement Externe - 6 étapes */}
    <Card className="border-none shadow-xl bg-gradient-to-br from-slate-800 to-gray-900 text-white">
      <CardHeader>
        <CardTitle className="text-2xl text-white">Lien de Paiement Externe — <span className="text-cyan-400">Fonctionnalité Unique</span></CardTitle>
        <CardDescription className="text-gray-300">Parent gère la commande complète, tiers paie via interface dédiée</CardDescription>
      </CardHeader>
      <CardContent>
        <h4 className="font-bold text-lg mb-4 text-gray-200">Comment ça Fonctionne</h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">1</div>
            <p className="font-bold text-blue-300 mb-1">Parent crée la commande complète</p>
            <p className="text-sm text-gray-400">Configure tous les repas, menus et préférences pour son/ses enfant(s) via son portail</p>
          </div>
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">2</div>
            <p className="font-bold text-blue-300 mb-1">Génération lien personnalisé</p>
            <p className="text-sm text-gray-400">Système génère un lien de paiement unique et sécurisé pour cette commande spécifique</p>
          </div>
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">3</div>
            <p className="font-bold text-blue-300 mb-1">Envoi au payeur tiers</p>
            <p className="text-sm text-gray-400">Parent envoie le lien (email, SMS, WhatsApp) à la personne qui va effectuer le paiement</p>
          </div>
          <div className="bg-cyan-900/40 p-4 rounded-xl border border-cyan-500/30">
            <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mb-3">4</div>
            <p className="font-bold text-cyan-300 mb-1">Interface frontend dédiée</p>
            <p className="text-sm text-gray-400">Tiers accède à une page web simple et sécurisée affichant la commande et le montant à payer</p>
          </div>
          <div className="bg-pink-900/40 p-4 rounded-xl border border-pink-500/30">
            <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center text-white font-bold mb-3">5</div>
            <p className="font-bold text-pink-300 mb-1">Paiement sans inscription</p>
            <p className="text-sm text-gray-400">Tiers effectue le paiement directement via plateforme (carte bancaire) sans créer de compte</p>
          </div>
          <div className="bg-green-900/40 p-4 rounded-xl border border-green-500/30">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mb-3">6</div>
            <p className="font-bold text-green-300 mb-1">Confirmation instantanée</p>
            <p className="text-sm text-gray-400">Parent et tiers reçoivent confirmation immédiate avec reçu de paiement</p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Avantages Parent / Tiers */}
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center space-x-2">
            <Star className="w-5 h-5 text-blue-600" />
            <span>Avantages pour Parent</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-3"><Users className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Contrôle total sur commande et choix alimentaires</p></div>
          <div className="flex items-start space-x-3"><BarChart3 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Flexibilité financière — déléguer paiement facilement</p></div>
          <div className="flex items-start space-x-3"><Eye className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Transparence — tiers voit exactement ce qu'il paie</p></div>
          <div className="flex items-start space-x-3"><Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Historique complet de qui a payé quoi et quand</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-amber-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center space-x-2">
            <Award className="w-5 h-5 text-orange-600" />
            <span>Avantages pour Tiers</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-3"><Zap className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Ultra simple — 1 clic, paiement, terminé</p></div>
          <div className="flex items-start space-x-3"><FileText className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Aucune inscription requise pour payer</p></div>
          <div className="flex items-start space-x-3"><Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Sécurité — paiement via plateforme certifiée</p></div>
          <div className="flex items-start space-x-3"><Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Reçu instantané par email pour preuve</p></div>
        </CardContent>
      </Card>
    </div>

    {/* Cas d'Usage Typiques */}
    <Card className="border-none shadow-xl bg-gradient-to-br from-slate-800 to-blue-900 text-white">
      <CardHeader>
        <CardTitle className="text-xl text-white flex items-center space-x-2">
          <Star className="w-5 h-5 text-yellow-400" />
          <span>Cas d'Usage Typiques</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/10 p-5 rounded-xl text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-cyan-400" />
            <p className="font-bold text-lg mb-1">Grands-Parents</p>
            <p className="text-sm text-gray-300">Contribuent aux repas de leurs petits-enfants</p>
          </div>
          <div className="bg-white/10 p-5 rounded-xl text-center">
            <Gift className="w-10 h-10 mx-auto mb-3 text-pink-400" />
            <p className="font-bold text-lg mb-1">Cadeau Anniversaire</p>
            <p className="text-sm text-gray-300">Offrir des repas scolaires comme cadeau utile</p>
          </div>
          <div className="bg-white/10 p-5 rounded-xl text-center">
            <Heart className="w-10 h-10 mx-auto mb-3 text-red-400" />
            <p className="font-bold text-lg mb-1">Famille Élargie</p>
            <p className="text-sm text-gray-300">Oncles, tantes, parrains qui veulent aider</p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Scénarios d'Usage */}
    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-xl">Scénarios d'Usage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
            <Scale className="w-8 h-8 text-blue-600 mb-2" />
            <h4 className="font-bold text-blue-700 mb-2">Garde Partagée</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Semaine A/B</li>
              <li>- Week-ends alternés</li>
              <li>- Vacances scolaires</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-5 rounded-xl border border-cyan-200">
            <Users className="w-8 h-8 text-cyan-600 mb-2" />
            <h4 className="font-bold text-cyan-700 mb-2">Grands-Parents Impliqués</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Accès lecture seule</li>
              <li>- Suivi des enfants</li>
              <li>- Participation financière optionnelle</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-5 rounded-xl border border-pink-200">
            <Heart className="w-8 h-8 text-pink-600 mb-2" />
            <h4 className="font-bold text-pink-700 mb-2">Parents Séparés</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Transparence totale</li>
              <li>- Évite conflits financiers</li>
              <li>- Communication fluide</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl border border-purple-200">
            <Shield className="w-8 h-8 text-purple-600 mb-2" />
            <h4 className="font-bold text-purple-700 mb-2">Tuteurs Multiples Légaux</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Droits configurables</li>
              <li>- Gestion multi-acteurs</li>
              <li>- Conformité légale</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Avantages Clés */}
    <Card className="border-none shadow-lg bg-gradient-to-br from-gray-50 to-slate-100">
      <CardHeader>
        <CardTitle className="text-xl">Avantages Clés</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3 p-3">
            <Eye className="w-6 h-6 text-blue-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Transparence Totale</p><p className="text-xs text-gray-500">Toutes les actions visibles — Historique partagé complet</p></div>
          </div>
          <div className="flex items-start space-x-3 p-3">
            <RefreshCw className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Évite les Doublons</p><p className="text-xs text-gray-500">Commandes synchronisées — Alertes anti-doublon</p></div>
          </div>
          <div className="flex items-start space-x-3 p-3">
            <MessageSquare className="w-6 h-6 text-cyan-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Communication Fluide</p><p className="text-xs text-gray-500">Notifications contextuelles — Validation 1 clic</p></div>
          </div>
          <div className="flex items-start space-x-3 p-3">
            <CreditCard className="w-6 h-6 text-orange-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Gestion Financière Claire</p><p className="text-xs text-gray-500">Split automatique — Factures séparées — Rapports détaillés</p></div>
          </div>
          <div className="flex items-start space-x-3 p-3">
            <Lock className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Confidentialité Garantie</p><p className="text-xs text-gray-500">Données bancaires chiffrées — Permissions granulaires</p></div>
          </div>
          <div className="flex items-start space-x-3 p-3">
            <RefreshCw className="w-6 h-6 text-purple-500 flex-shrink-0" />
            <div><p className="font-bold text-gray-800 text-sm">Synchro Temps Réel</p><p className="text-xs text-gray-500">Mise à jour instantanée — 0 conflit</p></div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Comment Ça Marche */}
    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Comment Ça Marche</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
          <div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Parent principal <strong className="text-blue-600">configure et invite</strong> l'autre parent</p></div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
          <div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Parent invité <strong className="text-blue-600">accepte et crée son compte</strong></p></div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
          <div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Les deux parents <strong className="text-blue-600">valident le partage paiement</strong></p></div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
          <div className="pl-4"><p className="text-gray-700">Les transactions futures sont <strong className="text-green-600">automatiquement partagées</strong></p></div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const TraiteurSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1675647699232-76b8f533b006" 
        alt="Traiteur" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-orange-600/90 to-amber-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Portail Traiteur</h2>
          <p className="text-xl">Back-office complet de gestion</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <ChefHat className="w-6 h-6 text-orange-600" />
          <span>Gestion Complète de l'Activité Scolaire</span>
        </CardTitle>
        <CardDescription>
          Un seul panneau pour gérer toute l'activité : commandes, menus, production, paiements et rapports
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><ClipboardList className="w-4 h-4 inline text-orange-400" /> Commandes</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Affichage et tri</li>
              <li>• Annulations / Modifications</li>
              <li>• Crédits automatiques</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><Utensils className="w-4 h-4 inline text-orange-400" /> Menus</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Duplication facile</li>
              <li>• Gestion jours fériés</li>
              <li>• Variations par école</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><Building2 className="w-4 h-4 inline text-orange-400" /> Production</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Agrégation automatique</li>
              <li>• Volumes en temps réel</li>
              <li>• Planification optimisée</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><CreditCard className="w-4 h-4 inline text-orange-400" /> Comptabilité</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• QuickBooks</li>
              <li>• Sage</li>
              <li>• Acomba / Acomba Go</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><CreditCard className="w-4 h-4 inline text-orange-400" /> Paiements</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Clover</li>
              <li>• Sezzle</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><Bell className="w-4 h-4 inline text-orange-400" /> Alertes</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Plats sous seuil</li>
              <li>• Automatiques / personnalisées</li>
              <li>• Notifications push</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><BarChart3 className="w-4 h-4 inline text-orange-400" /> Rapports</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Plats Top / Flop</li>
              <li>• Participation</li>
              <li>• Automatisation</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
            <h4 className="font-bold text-orange-600 mb-2"><MessageSquare className="w-4 h-4 inline text-cyan-400" /> Communication</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Canal école ↔ traiteur</li>
              <li>• Chat intégré</li>
              <li>• Partage de fichiers</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-orange-50 to-amber-50">
      <CardHeader>
        <CardTitle className="text-xl">Logique et Interactions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
          <p className="text-gray-700">Le traiteur crée ses menus → <strong className="text-orange-600">synchronisés</strong> avec les écoles</p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
          <p className="text-gray-700">Parent commande → données <strong className="text-orange-600">remontent automatiquement</strong> dans le back-office</p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
          <p className="text-gray-700">Rapports de production <strong className="text-orange-600">préparés automatiquement</strong> chaque jour</p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
          <p className="text-gray-700">En cas de problème → <strong className="text-orange-600">alerte automatique</strong> déclenchée</p>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl flex items-center space-x-2">
          <Shield className="w-5 h-5 text-orange-600" />
          <span>Sécurité et Confidentialité</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5" />
            <p className="text-gray-700">Chaque traiteur opère dans une <strong>bulle privée</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5" />
            <p className="text-gray-700">Les écoles et CSS ne voient que les <strong>agrégats</strong> (jamais les détails internes)</p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-orange-600 mt-0.5" />
            <p className="text-gray-700">Protection complète des données commerciales et financières</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const ParentSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1551650975-87deedd944c3" 
        alt="Parent" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600/90 to-pink-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Portail Parent / Enfant</h2>
          <p className="text-xl">Simple, intuitif, rapide et ludique</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Users className="w-6 h-6 text-purple-600" />
          <span>Simplifier la Vie des Familles</span>
        </CardTitle>
        <CardDescription>
          Interface intuitive pour les parents et expérience ludique pour les enfants
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-bold text-purple-600 mb-4 flex items-center space-x-2">
              <span><Users className="w-4 h-4" /></span>
              <span>Mode Parent</span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Ordinateur</strong>
                  <p className="text-sm text-gray-600">Interface web complète</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Application Android & Apple</strong>
                  <p className="text-sm text-gray-600">Applications mobiles natives</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Commande rapide multi-enfants</strong>
                  <p className="text-sm text-gray-600">Quick Order : 1 clic = tous les enfants</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Couleurs personnalisées</strong>
                  <p className="text-sm text-gray-600">Pour chaque enfant, simplifie la gestion</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Personnalisation hebdomadaire, mensuelle ou annuelle des repas</strong>
                  <p className="text-sm text-gray-600">Planification flexible court, moyen et long terme</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Abonnements automatiques</strong>
                  <p className="text-sm text-gray-600">Paiement récurrent ou ponctuel</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Paiements flexibles</strong>
                  <p className="text-sm text-gray-600">Portefeuille & modes de paiement</p>
                  <p className="text-sm text-gray-600">Gérez cartes, Sezzle et solde interne facilement</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Notifications personnalisées</strong>
                  <p className="text-sm text-gray-600">Rappels et confirmations</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Historique et reçus</strong>
                  <p className="text-sm text-gray-600">Suivi complet des commandes</p>
                </div>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold text-pink-600 mb-4 flex items-center space-x-2">
              <span><Baby className="w-4 h-4" /></span>
              <span>Mode Enfant</span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Jeu interactif</strong>
                  <p className="text-sm text-gray-600">Commande ludique et amusante</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Création d'avatar</strong>
                  <p className="text-sm text-gray-600">Avatar personnalisé pour chaque enfant</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Gestion portail</strong>
                  <p className="text-sm text-gray-600">Interface adaptée aux enfants</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Thèmes personnalisés</strong>
                  <p className="text-sm text-gray-600">Choix de thèmes et couleurs</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Système de badges</strong>
                  <p className="text-sm text-gray-600">Récompenses pour participation</p>
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Implication active</strong>
                  <p className="text-sm text-gray-600">Développe l'autonomie alimentaire</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-xl">Logique UX</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Zap className="w-4 h-4" /></div>
            <p className="text-gray-700"><strong>1 clic</strong> = tous les enfants commandés</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Smartphone className="w-4 h-4" /></div>
            <p className="text-gray-700">Interface <strong>mobile-first</strong></p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Bell className="w-4 h-4" /></div>
            <p className="text-gray-700">Rappels si repas <strong>non noté</strong></p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Lightbulb className="w-4 h-4" /></div>
            <p className="text-gray-700">Suggestions et commentaires <strong>facultatifs</strong></p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <span>Confidentialité</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Aucune donnée globale visible</p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Pas de statistiques générales accessibles</p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Feedbacks <strong>anonymisés et pondérés</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Protection complète des données familiales</p>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-100 to-pink-100">
      <CardContent className="py-8">
        <div className="text-center">
          <div className="inline-block bg-white px-8 py-6 rounded-2xl shadow-lg max-w-2xl">
            <p className="text-3xl font-bold text-purple-600 mb-3"><Zap className="w-5 h-5 inline text-purple-400" /> 15 secondes</p>
            <p className="text-gray-700 text-lg leading-relaxed">pour commander les repas de tous vos enfants — chacun son menu, pour la semaine, le mois ou l'année !</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const EcoleSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.pexels.com/photos/1001914/pexels-photo-1001914.jpeg" 
        alt="École" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/90 to-blue-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Portail École</h2>
          <p className="text-xl">Cockpit de gestion et communication</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <School className="w-6 h-6 text-indigo-600" />
          <span>Cockpit de Gestion Scolaire</span>
        </CardTitle>
        <CardDescription>
          Outil de pilotage pour la satisfaction alimentaire et les opérations quotidiennes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-lg">
            <BarChart3 className="w-6 h-6 text-indigo-600 mb-2" />
            <h4 className="font-bold text-indigo-600 mb-2">Vue Quotidienne</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Repas commandés</li>
              <li>- Gestion des absences</li>
              <li>- Ajustements temps réel</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg">
            <Star className="w-6 h-6 text-yellow-600 mb-2" />
            <h4 className="font-bold text-yellow-600 mb-2">Satisfaction</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Score moyen 30 jours</li>
              <li>- Tendances d'évolution</li>
              <li>- Analyse par plat</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg">
            <Bell className="w-6 h-6 text-orange-600 mb-2" />
            <h4 className="font-bold text-orange-600 mb-2">Alertes</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Plats sous seuil</li>
              <li>- Retards de livraison</li>
              <li>- Automatiques</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
            <MessageSquare className="w-6 h-6 text-blue-600 mb-2" />
            <h4 className="font-bold text-blue-600 mb-2">Communication</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Canal direct traiteur</li>
              <li>- Direction d'école</li>
              <li>- Chat intégré</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
            <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
            <h4 className="font-bold text-green-600 mb-2">Rapports</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Automatisables</li>
              <li>- Personnalisables</li>
              <li>- Format flexible</li>
              <li>- Fréquence ajustable</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg">
            <Download className="w-6 h-6 text-purple-600 mb-2" />
            <h4 className="font-bold text-purple-600 mb-2">Exports</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>- Excel / PDF</li>
              <li>- Listes de classes</li>
              <li>- Rapports personnalisés</li>
            </ul>
          </div>
        </div>

        {/* Paramétrages & Permissions */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-600" />
              <span>Paramétrages</span>
            </h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Seuil de satisfaction configurable</p><p className="text-xs text-gray-500">Ex : alerte si &lt; 50%</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Délai avant alerte</p><p className="text-xs text-gray-500">Ex : après 3 repas consécutifs</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Rapports automatiques</p><p className="text-xs text-gray-500">Planification flexible</p></div></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center space-x-2">
              <Shield className="w-5 h-5 text-gray-600" />
              <span>Permissions</span>
            </h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Accès limité à l'établissement concerné</p></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Rôles multiples</p><p className="text-xs text-gray-500">Directeur, Secrétaire, Gestionnaire</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Aucun accès aux données des autres écoles</p></div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-indigo-50 to-blue-50">
      <CardHeader>
        <CardTitle className="text-xl">Fonctionnalités Clés</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
            <p className="text-gray-700">Suivi des <strong className="text-indigo-600">volumes de commandes</strong> en temps réel</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
            <p className="text-gray-700">Gestion des <strong className="text-indigo-600">profils élèves</strong> et allergies alimentaires</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
            <p className="text-gray-700">Communication directe avec <strong className="text-indigo-600">traiteurs partenaires</strong></p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
            <p className="text-gray-700">Génération de <strong className="text-indigo-600">rapports automatisés</strong></p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Continue with other sections...
const RegionSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.pexels.com/photos/6981024/pexels-photo-6981024.jpeg" 
        alt="Région" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-teal-600/90 to-green-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Direction d'École</h2>
          <p className="text-xl">Gestion de portefeuille d'établissements</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Building2 className="w-6 h-6 text-teal-600" />
          <span>Coordination Territoriale</span>
        </CardTitle>
        <CardDescription>
          Supervision et coordination entre terrain, traiteurs et CSS
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><BarChart3 className="w-4 h-4 inline text-teal-400" /> Vue Consolidée</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Volumes agrégés par école</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Satisfaction moyenne du portefeuille</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Alertes actives en temps réel</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><TrendingUp className="w-4 h-4 inline text-teal-400" /> Analyse Comparative</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Performance traiteur / école</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Comparatifs de portefeuille</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Identification des tendances</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><ClipboardList className="w-4 h-4 inline text-teal-400" /> Rapports de Portefeuille</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Export PDF/Excel automatisés</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Rapports personnalisables</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Planification flexible</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><MessageSquare className="w-4 h-4 inline text-cyan-400" /> Communication</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Canal écoles ↔ traiteurs</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Liaison avec CSS</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Chat multi-acteurs</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Journalisation, Gouvernance, Communication */}
    <div className="grid md:grid-cols-3 gap-4">
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center space-x-2">
            <Shield className="w-5 h-5 text-orange-500" />
            <span>Journalisation & Audit</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Toutes les actions sont traçables</p></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Audit complet</p><p className="text-xs text-gray-500">Lecture, modification, communication</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Conformité et transparence garanties</p></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Historique complet des décisions</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center space-x-2">
            <Bell className="w-5 h-5 text-pink-500" />
            <span>Gouvernance & Alertes</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Indicateurs stratégiques</p><p className="text-xs text-gray-500">Tableaux de bord en temps réel</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Alertes critiques automatiques</p><p className="text-xs text-gray-500">Notifications immédiates</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Règles paramétrables</p><p className="text-xs text-gray-500">Seuils, fréquences, priorités</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Escalade automatique si nécessaire</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-pink-500" />
            <span>Communication</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Politiques alimentaires</p><p className="text-xs text-gray-500">Diffusion et mise en oeuvre</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Directives opérationnelles</p><p className="text-xs text-gray-500">Communications officielles</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Normes et standards</p><p className="text-xs text-gray-500">Conformité réglementaire</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Canal multi-acteurs intégré</p></div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-teal-50 to-green-50">
      <CardHeader>
        <CardTitle className="text-xl">Logique d'Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
          <p className="text-gray-700">Chaque direction d'école gère <strong className="text-teal-600">son école ou un portefeuille d'établissements</strong></p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
          <p className="text-gray-700">Peut <strong className="text-teal-600">intervenir avant</strong> que le CSS n'ait à le faire</p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
          <p className="text-gray-700">Les alertes peuvent être automatiquement <strong className="text-teal-600">escaladées au CSS par le canal interne</strong></p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
          <p className="text-gray-700">Paramétrages de portefeuille : <strong className="text-teal-600">seuils, fréquences, priorités</strong></p>
        </div>
      </CardContent>
    </Card>
  </div>
);

const CSSSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1519389950473-47ba0277781c" 
        alt="CSS" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/90 to-rose-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Portail CSS</h2>
          <p className="text-xl">Gouvernance alimentaire globale</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Landmark className="w-6 h-6 text-red-600" />
          <span>Centre de Gouvernance</span>
        </CardTitle>
        <CardDescription>
          Centralisation complète de la gouvernance alimentaire scolaire
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><Network className="w-4 h-4 inline text-red-400" /> Vue Globale</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Toutes les directions</li>
              <li>• Toutes les écoles</li>
              <li>• Tous les traiteurs</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><BarChart3 className="w-4 h-4 inline text-red-400" /> Comparatifs</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Performance</li>
              <li>• Qualité</li>
              <li>• Conformité</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><TrendingUp className="w-4 h-4 inline text-red-400" /> Rapports</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Mensuels / trimestriels / annuels</li>
              <li>• Personnalisables</li>
              <li>• Automatisations</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><Target className="w-4 h-4 inline text-red-400" /> Gouvernance</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Indicateurs stratégiques</li>
              <li>• Alertes critiques</li>
              <li>• Tableaux de bord</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><MessageSquare className="w-4 h-4 inline text-red-400" /> Communication</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Politiques</li>
              <li>• Directives</li>
              <li>• Normes</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg">
            <h4 className="font-bold text-red-600 mb-2"><TrendingUp className="w-4 h-4 inline text-red-400" /> Durabilité</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Menus santé</li>
              <li>• Gaspillage</li>
              <li>• Produits locaux</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-red-50 to-rose-50">
        <CardHeader>
          <CardTitle className="text-xl">Gouvernance Flexible</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Contrôle total</strong>
              <p className="text-sm text-gray-600">Toutes les permissions et accès</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Communication directe</strong>
              <p className="text-sm text-gray-600">Avec traiteurs ou via directions d'école</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Délégation flexible</strong>
              <p className="text-sm text-gray-600">Aux directions d'école selon besoins</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Règles paramétrables</strong>
              <p className="text-sm text-gray-600">Escalade, alertes et rapports</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-600" />
            <span>Journalisation</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-gray-700">Toutes les actions sont <strong>traçables</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Audit complet</strong>
              <p className="text-sm text-gray-600">Lecture, modification, communication</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-gray-700">Conformité et transparence garanties</p>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const EvaluationSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1514575110897-1253ff7b2ccb" 
        alt="Evaluation" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/90 to-orange-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Système d'Évaluation</h2>
          <p className="text-xl">Mesurer la satisfaction réelle</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Star className="w-6 h-6 text-yellow-600" />
          <span>Notation Universelle à 3 Niveaux</span>
        </CardTitle>
        <CardDescription>
          Simple, intuitif et efficace pour tous les âges
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center border-2 border-green-200">
            <div className="text-6xl mb-3"><Star className="w-12 h-12 text-green-400" /></div>
            <div className="font-bold text-2xl text-green-600 mb-2">+1</div>
            <div className="text-green-700 font-semibold">Aimé</div>
            <div className="mt-2 px-3 py-1 bg-green-500 text-white rounded-full text-sm inline-block">Vert</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-6 rounded-xl text-center border-2 border-yellow-200">
            <div className="text-6xl mb-3"><AlertTriangle className="w-12 h-12 text-yellow-400" /></div>
            <div className="font-bold text-2xl text-yellow-600 mb-2">0</div>
            <div className="text-yellow-700 font-semibold">Neutre</div>
            <div className="mt-2 px-3 py-1 bg-yellow-500 text-white rounded-full text-sm inline-block">Jaune</div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-xl text-center border-2 border-red-200">
            <div className="text-6xl mb-3"><X className="w-12 h-12 text-red-400" /></div>
            <div className="font-bold text-2xl text-red-600 mb-2">-1</div>
            <div className="text-red-700 font-semibold">Pas aimé</div>
            <div className="mt-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm inline-block">Rouge</div>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle className="text-xl">Pondération Intelligente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>Fenêtre de 30 jours glissants</strong>
              <p className="text-sm text-gray-600">Évaluation sur période récente</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>Repas récents prioritaires</strong>
              <p className="text-sm text-gray-600">Comptent davantage dans le calcul</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>Calculs automatiques</strong>
              <p className="text-sm text-gray-600">Moyennes et tendances en temps réel</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">Évolution et Alertes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <Bell className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <strong>Flèches et % d'évolution</strong>
              <p className="text-sm text-gray-600">Affichés dans portails école et traiteur</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Bell className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <strong>Alerte automatique</strong>
              <p className="text-sm text-gray-600">Si score &lt; seuil configuré</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <strong>Analyse de tendances</strong>
              <p className="text-sm text-gray-600">Identification des patterns</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Interaction Ludique</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-bold text-purple-600 mb-3"><Layout className="w-4 h-4 inline text-purple-400" /> Mode Enfant</h4>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">Jeu animé et interactif</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">Système de badges / Récompenses ludiques</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">Avatar 100% personnalisables</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-pink-600 mb-3"><Users className="w-4 h-4 inline text-cyan-400" /> Mode Parent</h4>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Smiley statique simple</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Interface épurée</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Évaluation rapide</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const AlertesSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-rose-600/90 to-pink-600/90 flex items-center justify-center">
      <div className="text-center text-white p-6">
        <Bell className="w-20 h-20 mx-auto mb-4" />
        <h2 className="text-4xl font-bold mb-4">Alertes & Rapports</h2>
        <p className="text-xl">Système intelligent de notification</p>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Bell className="w-6 h-6 text-rose-600" />
          <span>Types d'Alertes</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-500">
            <h4 className="font-bold text-red-600 mb-2 flex items-center space-x-2">
              <span className="w-3 h-3 bg-red-500 rounded-full inline-block"></span>
              <span>Qualité</span>
            </h4>
            <p className="text-sm text-gray-700">Plats sous seuil de satisfaction configuré</p>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-500">
            <h4 className="font-bold text-orange-600 mb-2 flex items-center space-x-2">
              <span className="w-3 h-3 bg-orange-500 rounded-full inline-block"></span>
              <span>Logistique</span>
            </h4>
            <p className="text-sm text-gray-700">Retards de livraison, problèmes d'approvisionnement</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-500">
            <h4 className="font-bold text-blue-600 mb-2 flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full inline-block"></span>
              <span>Participation</span>
            </h4>
            <p className="text-sm text-gray-700">Baisse significative des commandes</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-bold text-yellow-600 mb-2 flex items-center space-x-2">
              <span className="w-3 h-3 bg-yellow-500 rounded-full inline-block"></span>
              <span>Tendance négative</span>
            </h4>
            <p className="text-sm text-gray-700">Détérioration progressive de la satisfaction</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-rose-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Escalade Hiérarchique</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">École</div>
              <p className="text-sm text-gray-600">Première intervention et gestion locale</p>
            </div>
          </div>
          <div className="ml-6 w-1 h-8 bg-gray-300"></div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">Direction d'École</div>
              <p className="text-sm text-gray-600">Si non résolue après délai configuré</p>
            </div>
          </div>
          <div className="ml-6 w-1 h-8 bg-gray-300"></div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">CSS</div>
              <p className="text-sm text-gray-600">Escalade finale pour décision stratégique</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white rounded-lg border-2 border-rose-200">
            <p className="text-center text-rose-600 font-medium">Chaque niveau peut intervenir, annoter ou clôturer une alerte</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Rapports Personnalisables</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-3"><FileText className="w-4 h-4 inline text-blue-400" /> Contenu</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Plats Top / Flop</li>
              <li>• Évolution satisfaction</li>
              <li>• Participation</li>
              <li>• Alertes actives</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
            <h4 className="font-bold text-purple-600 mb-3"><Clock className="w-4 h-4 inline text-purple-400" /> Fréquence</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Quotidien</li>
              <li>• Hebdomadaire</li>
              <li>• Mensuel</li>
              <li>• Sur demande</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
            <h4 className="font-bold text-green-600 mb-3"><BarChart3 className="w-4 h-4 inline text-green-400" /> Format</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• PDF élaboré</li>
              <li>• Excel détaillé</li>
              <li>• Graphiques interactifs</li>
              <li>• Email automatique</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const SecuriteSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-gray-700/90 to-slate-700/90 flex items-center justify-center">
      <div className="text-center text-white p-6">
        <Shield className="w-20 h-20 mx-auto mb-4" />
        <h2 className="text-4xl font-bold mb-4">Sécurité & Confidentialité</h2>
        <p className="text-xl">Protection absolue des données</p>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Shield className="w-6 h-6 text-gray-700" />
          <span>Architecture Sécurisée</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg border-2 border-gray-200">
            <h4 className="font-bold text-gray-700 mb-4 flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Multi-tenant isolé</span>
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Chaque entité = espace protégé</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Isolation complète des données</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Aucune fuite possible entre tenants</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg border-2 border-gray-200">
            <h4 className="font-bold text-gray-700 mb-4 flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Données filtrées</span>
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Via le back-end central sécurisé</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Principe du "besoin de savoir"</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Transmission sélective uniquement</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Permissions (RBAC)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-2">Role-Based Access Control</h4>
            <p className="text-gray-700">Rôles et droits précis pour chaque utilisateur selon sa fonction</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">P</div>
                <span>Parent</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Ses enfants uniquement</li>
                <li>• Ses commandes</li>
                <li>• Ses paiements</li>
                <li>• Historique personnel</li>
                <li>• Évaluations de ses enfants</li>
                <li>• Notifications personnelles</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">T</div>
                <span>Traiteur</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Ses commandes</li>
                <li>• Ses écoles clientes</li>
                <li>• Gestion des menus</li>
                <li>• Statistiques de vente</li>
                <li>• Paiements reçus</li>
                <li>• Alertes opérationnelles</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">É</div>
                <span>École</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Son établissement</li>
                <li>• Ses statistiques</li>
                <li>• Ses alertes</li>
                <li>• Gestion des menus</li>
                <li>• Rapports locaux</li>
                <li>• Coordination traiteur</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div>
                <span>Direction d'École</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Son portefeuille d'écoles</li>
                <li>• Ses écoles</li>
                <li>• Rapports consolidés</li>
                <li>• Alertes escaladées</li>
                <li>• Supervision multi-écoles / multi-établissements</li>
                <li>• Analyse comparative</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">C</div>
                <span>CSS</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Vue globale</li>
                <li>• Toutes les données</li>
                <li>• Contrôle total</li>
                <li>• Gestion des politiques</li>
                <li>• Rapports stratégiques</li>
                <li>• Administration système</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-gray-50 to-slate-50">
      <CardHeader>
        <CardTitle className="text-xl">Sécurité des Données</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white"><Lock className="w-4 h-4" /></div>
            <div>
              <div className="font-bold text-gray-800">Chiffrement complet</div>
              <p className="text-sm text-gray-600">En transit (TLS/SSL) et au repos (AES-256)</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white"><FileText className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Audit log complet</div>
              <p className="text-sm text-gray-600">Traçabilité de toutes les actions et accès</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white"><Download className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Sauvegardes automatiques</div>
              <p className="text-sm text-gray-600">Backup quotidien avec rétention configurable</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white"><Shield className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Protection DDoS et WAF</div>
              <p className="text-sm text-gray-600">Pare-feu applicatif et protection contre les attaques</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const FluxSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-cyan-600/90 to-blue-600/90 flex items-center justify-center">
      <div className="text-center text-white p-6">
        <Network className="w-20 h-20 mx-auto mb-4" />
        <h2 className="text-4xl font-bold mb-4">Flux d'Information</h2>
        <p className="text-xl">Communication orchestrée</p>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Network className="w-6 h-6 text-cyan-600" />
          <span>Architecture des Flux</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-lg border-2 border-blue-200">
            <h4 className="font-bold text-blue-600 mb-4 text-lg">Flux Verticaux Ascendants</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">P</div>
                <span className="text-gray-700">Parents</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">E</div>
                <span className="text-gray-700">Écoles</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div>
                <span className="text-gray-700">Directions d'École</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">C</div>
                <span className="text-gray-700">CSS</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-100 rounded text-sm text-blue-700">
              <strong>Commandes, feedbacks, alertes</strong> remontent la hiérarchie
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
            <h4 className="font-bold text-green-600 mb-4 text-lg">Flux Verticaux Descendants</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">C</div>
                <span className="text-gray-700">CSS</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div>
                <span className="text-gray-700">Directions d'École</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">E</div>
                <span className="text-gray-700">Écoles</span>
              </div>
              <div className="ml-4 w-1 h-8 bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">T</div>
                <span className="text-gray-700">Traiteurs</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-green-100 rounded text-sm text-green-700">
              <strong>Politiques, directives, normes</strong> descendent la hiérarchie
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Flux Horizontaux</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">E</div>
                <span className="font-semibold">Écoles</span>
              </div>
              <span className="text-2xl text-purple-600">↔</span>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">T</div>
                <span className="font-semibold">Traiteurs</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-3">Communication directe pour gestion opérationnelle</p>
          </div>

          <div className="bg-white p-4 rounded-lg border-2 border-pink-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">T</div>
                <span className="font-semibold">Traiteurs</span>
              </div>
              <span className="text-2xl text-pink-600">↔</span>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">P</div>
                <span className="font-semibold">Parents</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-3">Commandes, confirmations, feedbacks</p>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg">
            <p className="text-center font-medium text-gray-700">
              Les flux horizontaux permettent une <strong className="text-purple-600">réactivité immédiate</strong> sans attendre l'escalade hiérarchique
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Rôle du Back-end Central</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-lg">
            <h4 className="font-bold text-cyan-600 mb-3"><Target className="w-4 h-4 inline text-cyan-400" /> Orchestration</h4>
            <p className="text-sm text-gray-700">Coordonne tous les flux d'information en temps réel</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-3"><Lock className="w-4 h-4 inline text-blue-400" /> Sécurité</h4>
            <p className="text-sm text-gray-700">Applique les règles d'accès et permissions</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg">
            <h4 className="font-bold text-indigo-600 mb-3"><PieChart className="w-4 h-4 inline text-indigo-400" /> Agrégation</h4>
            <p className="text-sm text-gray-700">Génère statistiques et rapports consolidés</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const ConclusionSection = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c" 
        alt="Conclusion" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-teal-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <CheckCircle2 className="w-20 h-20 mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">Conclusion</h2>
          <p className="text-xl">Bien plus qu'une simple plateforme</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-2xl bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <CardContent className="py-12">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">EcoLuncH</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              N'est pas qu'un simple système de commande —
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-green-600 mb-3">Une Révolution à l'Échelle du Québec</h3>
              <p className="text-gray-700">
                Une <strong>transformation structurelle</strong> du modèle alimentaire scolaire, déployée à la grandeur de la province
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-3xl mb-2"><Lock className="w-4 h-4" /></div>
                <h4 className="font-bold text-gray-800 mb-2">Sécurisée</h4>
                <p className="text-sm text-gray-600">Protection absolue des données</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-3xl mb-2"><Zap className="w-4 h-4" /></div>
                <h4 className="font-bold text-gray-800 mb-2">Automatisée</h4>
                <p className="text-sm text-gray-600">Réduction du travail manuel</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-3xl mb-2"><TrendingUp className="w-6 h-6" /></div>
                <h4 className="font-bold text-gray-800 mb-2">Évolutive</h4>
                <p className="text-sm text-gray-600">S'adapte aux besoins</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-teal-600 mb-3">Pour Chaque Acteur</h3>
              <p className="text-gray-700 mb-4">
                Des <strong>outils adaptés à son rôle</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Badge className="bg-purple-500 text-white px-4 py-2">Parents</Badge>
                <Badge className="bg-pink-500 text-white px-4 py-2">Enfants</Badge>
                <Badge className="bg-indigo-500 text-white px-4 py-2">Écoles</Badge>
                <Badge className="bg-orange-500 text-white px-4 py-2">Traiteurs</Badge>
                <Badge className="bg-teal-500 text-white px-4 py-2">Direction</Badge>
                <Badge className="bg-red-500 text-white px-4 py-2">CSS</Badge>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-8 rounded-xl shadow-xl text-white">
              <h3 className="text-2xl font-bold mb-3">Une Gouvernance Centralisée</h3>
              <p className="text-lg">
                <strong>Mesurable</strong> · <strong>Ajustable</strong> · <strong>Transparente</strong>
              </p>
              <p className="mt-4 text-green-50">
                La révolution alimentaire scolaire au Québec — une vision stratégique complète pour le CSS
              </p>
            </div>
          </div>

          <div className="pt-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full"
              >
                <source src="https://customer-assets.emergentagent.com/job_7f252a75-97cc-4ed7-9549-a7fb574469a2/artifacts/bq1qtgws_EcoLuncH%20Animated.mp4" type="video/mp4" />
              </video>
              <div className="px-8 py-6 text-center">
                <p className="text-gray-600 font-semibold">La révolution alimentaire scolaire au Québec</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardContent className="py-8">
        <div className="text-center space-y-4">
          <img 
            src="/ecolunch-logo.png"
            alt="EcoLuncH Logo"
            className="h-16 mx-auto mb-4"
          />
          <h3 className="text-xl font-bold text-gray-800">Prêt à Découvrir Plus ?</h3>
          <p className="text-gray-600">Explorez les différentes sections du media kit pour en savoir plus sur EcoLuncH</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Badge variant="outline" className="px-4 py-2"><Target className="w-3 h-3 inline text-green-400" /> Vision</Badge>
            <Badge variant="outline" className="px-4 py-2"><Network className="w-3 h-3 inline text-green-400" /> Architecture</Badge>
            <Badge variant="outline" className="px-4 py-2"><ChefHat className="w-3 h-3 inline text-green-400" /> Traiteur</Badge>
            <Badge variant="outline" className="px-4 py-2"><Users className="w-4 h-4 inline text-cyan-400" /> Parents</Badge>
            <Badge variant="outline" className="px-4 py-2"><School className="w-3 h-3 inline text-green-400" /> École</Badge>
            <Badge variant="outline" className="px-4 py-2"><Star className="w-3 h-3 inline text-yellow-400" /> Évaluation</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default MediaKit;